import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateVariantRequest,
  CreateVariantResponse,
  DuplicateVariantRequestParams,
  DuplicateVariantRequestPayload,
  EditVariantRequest,
  EditVariantResponse,
  GetVariantEditsAllowedResponse,
  GetVariantFinancialInputsResponse,
  GetVariantResponse,
  GetVariantsOfCompanyResponse,
  SuggestedVariantEditsResponse,
  VariantTailoringLinkResponse,
} from '@upright/common/model/input/company-variants/api-types';
import { chunkedAwaitMap } from '@upright/common/utils';
import { fetchOrThrow, fetchOrThrowNoReturnData } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { getEntityUpdateJobStatusQuery } from '@upright/ui/api/internal/jobs/entity-update-job-status';
import { useNavigate } from 'react-router-dom';
import { getCompanyVariantFreezeKey } from '../company-variant-freezes';

async function fetchVariantsOfCompany(companyId: string) {
  return await fetchOrThrow<GetVariantsOfCompanyResponse>(`/company/${companyId}/variants`, 'GET');
}

export function useVariantsOfCompany(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ['variantsOfCompany', companyId],

    queryFn: async () => {
      if (!companyId) throw Error('Unexpected.');
      return await fetchVariantsOfCompany(companyId);
    },

    enabled: !!companyId,
  });
}

async function fetchVariant(variantId: string) {
  return await fetchOrThrow<GetVariantResponse>(`/company-variants/${variantId}`);
}

export function useCompanyVariant(variantId: string | null | undefined) {
  return useQuery({
    queryKey: ['companyVariant', variantId],

    queryFn: async () => {
      if (!variantId) throw Error('Unexpected.');
      return await fetchVariant(variantId);
    },
    enabled: !!variantId,
  });
}

/**
 * @todo create a bulk endpoint
 */
export function useCompanyVariants(variantIds: string[] | null | undefined) {
  return useQuery({
    queryKey: ['companyVariant', variantIds],

    queryFn: async () => {
      if (!variantIds) throw Error('Unexpected.');
      return await chunkedAwaitMap(variantIds, fetchVariant);
    },

    enabled: !!variantIds,
  });
}

async function deleteVariant(variantId: string) {
  return await fetchOrThrowNoReturnData(`/company-variants/${variantId}`, 'DELETE');
}

export function useDeleteVariantMutation(redirect: boolean) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationKey: ['deleteVariant'],

    mutationFn: async (variantId: string) => {
      return await deleteVariant(variantId);
    },

    onSuccess: () => {
      if (redirect) {
        navigate('/internal/company-variants/create-new');
      }
    },

    onSettled: async (_data, _error, variantId) => {
      await queryClient.invalidateQueries({
        queryKey: ['variantsOfCompany'],
      });
      await queryClient.invalidateQueries({
        queryKey: ['companyVariant', variantId],
      });
    },
  });
}

async function createVariant(variantCreationPayload: CreateVariantRequest) {
  return await fetchOrThrow<CreateVariantResponse>(`/company-variants`, 'POST', variantCreationPayload);
}

export function useCreateVariantMutation() {
  const navigate = useNavigate();
  return useMutation({
    mutationKey: ['createVariant'],
    mutationFn: async (payload: CreateVariantRequest) => await createVariant(payload),

    onSuccess: response => {
      navigate(`/internal/company-variants/${response.id}`);
    },
  });
}

async function duplicateVariant(params: DuplicateVariantRequestParams, payload: DuplicateVariantRequestPayload) {
  return await fetchOrThrow(`/company-variants/${params.variantId}/duplicate`, 'POST', payload);
}

export function useDuplicateVariantMutation(variantId: string) {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, DuplicateVariantRequestPayload>({
    mutationKey: ['duplicateVariant', variantId],
    mutationFn: async (payload: DuplicateVariantRequestPayload) => {
      return await duplicateVariant({ variantId }, payload);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['companyVariant', variantId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['variantsOfCompany'],
      });
      await queryClient.invalidateQueries({
        queryKey: getEntityUpdateJobStatusQuery({ id: variantId, type: 'companyVariant' }).queryKey,
      });
    },
  });
}

async function createTailoringVariant(variantId: string) {
  return await fetchOrThrow(`/company-variants/${variantId}/tailoring-variant`, 'POST');
}

export function useCreateTailoringVariant(variantId: string, onError?: (error: Error) => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['tailoringVariant', variantId],
    mutationFn: async () => {
      return await createTailoringVariant(variantId);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['companyVariant', variantId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['variantsOfCompany'],
      });
    },
    onError: onError,
  });
}

export function useGetVariantTailoringLink(variantId: string) {
  return useQuery({
    queryKey: ['variantTailoringLink', variantId],

    queryFn: async () => {
      return await fetchVariantTailoringLink(variantId);
    },
  });
}

async function fetchVariantTailoringLink(variantId: string) {
  return await fetchOrThrow<VariantTailoringLinkResponse>(`/company-variants/${variantId}/tailoring-link`);
}

async function fetchVariantFinancialInputs(variantId: string) {
  return await fetchOrThrow<GetVariantFinancialInputsResponse>(`/company-variants/${variantId}/financial-inputs`);
}

/**
 * The financial-statement / FE-parameter / FE-version ids the variant points at.
 *
 * Use this rather than `useCompanyVariant` on any surface a customer can reach:
 * `useCompanyVariant` hits the full variant endpoint, which is team-only because its
 * response also carries per-dataset-gated result figures.
 */
export function useCompanyVariantFinancialInputs(variantId: string | null | undefined) {
  return useQuery({
    // Nested under the `['companyVariant', variantId]` prefix on purpose: everything that
    // relinks a variant's inputs — saving a statement, importing FE parameters, editing the
    // variant — already invalidates that prefix, and react-query's prefix matching then
    // refreshes this query too. A sibling top-level key would silently miss those.
    queryKey: ['companyVariant', variantId, 'financialInputs'],
    queryFn: async () => {
      if (!variantId) throw Error('Unexpected.');
      return await fetchVariantFinancialInputs(variantId);
    },
    enabled: !!variantId,
  });
}

async function fetchVariantEditsAllowed(variantId: string) {
  return await fetchOrThrow<GetVariantEditsAllowedResponse>(`/company-variants/${variantId}/edits-allowed`);
}

export function useVariantEditsAllowed(variantId: string | null | undefined) {
  return useQuery({
    queryKey: ['variantEditsAllowed', variantId],
    queryFn: async () => {
      if (!variantId) throw Error('Unexpected.');
      return await fetchVariantEditsAllowed(variantId);
    },
    enabled: !!variantId,
  });
}

async function editVariant(variantId: string, payload: EditVariantRequest) {
  return await fetchOrThrow<EditVariantResponse>(`/company-variants/${variantId}`, 'PATCH', payload);
}

export function useEditVariantMutation(variantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['editVariant', variantId],
    mutationFn: async (payload: EditVariantRequest) => await editVariant(variantId, payload),
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['companyVariant', variantId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['companyVariantEdits', variantId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['variantEditsAllowed', variantId],
      });
      await queryClient.invalidateQueries({
        queryKey: getEntityUpdateJobStatusQuery({ id: variantId, type: 'companyVariant' }).queryKey,
      });
    },
  });
}

export function useUnfreezeVariantMutation(variantId: string, freezeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['editVariant', variantId],
    mutationFn: async _ => await editVariant(variantId, { freezeId: null }),

    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ['companyVariant', variantId],
      });
      void queryClient.invalidateQueries({
        queryKey: getCompanyVariantFreezeKey(freezeId),
      });
    },

    onError: error => {
      alert(error);
    },
  });
}

export function useSuggestedCompanyVariantEdits(variantId: string) {
  return useQuery({
    queryKey: ['companyVariantEdits', variantId],
    queryFn: async () =>
      await fetchOrThrow<SuggestedVariantEditsResponse>(`/company-variants/${variantId}/suggested-edits`),
  });
}

async function queueVariantComputation(variantIds: string[]) {
  return await fetchOrThrow(`/company-variants/queue-computation`, 'POST', { variantIds });
}

export function useQueueVariantComputationMutation(variantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['queueVariantComputation', variantId],
    mutationFn: async () => await queueVariantComputation([variantId]),
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: getEntityUpdateJobStatusQuery({ id: variantId, type: 'companyVariant' }).queryKey,
      });
    },
  });
}
